import { Box } from '@chakra-ui/react';
import { type ComponentProps, type MutableRefObject, type Ref, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FlagIcon, FormField, Group, Icon, SelectNew, type SelectNewOption, Text, TextField } from '@melio/penny';

import {
  type CountryCode,
  type CountryOption,
  DEFAULT_COUNTRY,
  SUPPORTED_COUNTRIES,
  filterCountries,
  findCountry,
  formatAsYouType,
  isValidPhoneNumber,
  toE164,
} from './phoneNumber';

export interface PhoneNumberValue {
  country: CountryCode;
  nationalNumber: string;
}

export interface PhoneNumberChange extends PhoneNumberValue {
  /** E.164 representation, or undefined while the number can't yet be parsed. */
  e164: string | undefined;
  isValid: boolean;
}

export interface PhoneNumberFieldProps {
  label: string;
  /** Controlled value. Omit to let the field manage its own state. */
  value?: PhoneNumberValue;
  defaultCountry?: CountryCode;
  onChange: (value: PhoneNumberChange) => void;
  fieldProps?: Record<string, unknown>;
  testId?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
}

const stripFormatting = (value: string) => value.replace(/\D/g, '');

const mergeRefs =
  <T,>(...refs: Array<Ref<T> | undefined>): Ref<T> =>
  (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') ref(node);
      else (ref as MutableRefObject<T | null>).current = node;
    });
  };

const FIELD_BORDER_RADIUS = '8px';

export const PhoneNumberField = ({
  label,
  value,
  defaultCountry = DEFAULT_COUNTRY,
  onChange,
  fieldProps,
  testId = 'phone-number-input',
  isDisabled = false,
  isInvalid = false,
}: PhoneNumberFieldProps) => {
  const [internalValue, setInternalValue] = useState<PhoneNumberValue>(value ?? { country: defaultCountry, nationalNumber: '' });
  const [countrySearch, setCountrySearch] = useState('');
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const fieldGroupRef = useRef<HTMLDivElement>(null);
  // Set by handleNumberChange, applied once the reformatted value reaches the DOM.
  const pendingCursorPositionRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (pendingCursorPositionRef.current === null) return;
    const position = pendingCursorPositionRef.current;
    pendingCursorPositionRef.current = null;
    numberInputRef.current?.setSelectionRange(position, position);
  });
  // Trigger is narrow; measure the whole field so the dropdown can span it instead.
  const [menuWidth, setMenuWidth] = useState<number>();

  useEffect(() => {
    const el = fieldGroupRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setMenuWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const current = value ?? internalValue;
  const selectedCountry = findCountry(current.country);
  const toOption = (country: CountryOption): SelectNewOption<CountryCode> => ({
    value: country.code,
    label: `${country.flag} ${country.name} (+${country.callingCode})`,
    testId: country.code,
  });
  // Unfiltered, so the trigger can still resolve the selected value while a search hides it from the list.
  const countryOptions: SelectNewOption<CountryCode>[] = useMemo(() => SUPPORTED_COUNTRIES.map(toOption), []);
  const searchedCountryOptions: SelectNewOption<CountryCode>[] = useMemo(
    () => filterCountries(SUPPORTED_COUNTRIES, countrySearch).map(toOption),
    [countrySearch]
  );

  const emit = (next: PhoneNumberValue) => {
    setInternalValue(next);
    const isValid = isValidPhoneNumber(next.nationalNumber, next.country);
    onChange({
      ...next,
      e164: isValid ? toE164(next.nationalNumber, next.country) : undefined,
      isValid,
    });
  };

  const handleCountrySelect: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    // Clear button is hidden, so this never actually fires with a null value.
    const country = event.target.value as CountryCode;
    emit({
      country,
      nationalNumber: formatAsYouType(stripFormatting(current.nationalNumber), country),
    });
    // Deferred - SelectNew refocuses its own trigger on close, which would otherwise override this.
    setTimeout(() => numberInputRef.current?.focus(), 0);
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const cursorPosition = event.target.selectionStart ?? rawValue.length;
    const digitsBeforeCursor = stripFormatting(rawValue.slice(0, cursorPosition)).length;

    const nationalNumber = formatAsYouType(stripFormatting(rawValue), current.country);

    // Same digit count to the left, since formatting characters shift positions around.
    let digitsSeen = 0;
    let newCursorPosition = nationalNumber.length;
    for (let i = 0; i < nationalNumber.length; i++) {
      if (digitsSeen >= digitsBeforeCursor) {
        newCursorPosition = i;
        break;
      }
      if (/\d/.test(nationalNumber[i])) {
        digitsSeen++;
      }
    }
    pendingCursorPositionRef.current = newCursorPosition;

    emit({ country: current.country, nationalNumber });
  };

  const countryTriggerTestId = `${testId}-country-select`;

  const countryTrigger = (
    <Box
      // Width fits content, like a real "+<code> <number>" string - shifts with the code's length.
      width="fit-content"
      minWidth="72px"
      flexShrink={0}
      position="relative"
      // Once the menu opens, focus moves to the search bar in a portal (not a DOM descendant here),
      // so :focus-within alone can't detect it - track isOpen instead to keep this on top.
      zIndex={isCountryMenuOpen ? 1 : undefined}
      _focusWithin={{ zIndex: 1 }}
      sx={{
        // !important overrides Penny's own :focus-within border reset. Outline is left alone since,
        // unlike border, it doesn't affect layout.
        [`[data-testid="${countryTriggerTestId}-trigger"]`]: {
          borderRight: 'none !important',
          borderTopRightRadius: '0 !important',
          borderBottomRightRadius: '0 !important',
          paddingRight: '0 !important',
        },
        // Redundant with the flag/chevron/code trio already in valueRenderer.
        '[data-component="SelectNew.TriggerRightElement"]': {
          display: 'none',
        },
        // Country is required; nothing to clear to.
        [`[data-testid="${countryTriggerTestId}-clear-button"]`]: {
          display: 'none',
        },
      }}
    >
      <SelectNew
        options={countryOptions}
        value={current.country}
        onChange={handleCountrySelect}
        shouldHideClearButton
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        menuAriaLabel="Country"
        menuWidth={menuWidth ? `${menuWidth}px` : undefined}
        onOpenChange={setIsCountryMenuOpen}
        emptyState="No countries found"
        data-testid={countryTriggerTestId}
        aria-label={`Country: ${selectedCountry.name}`}
        searchBarProps={{
          value: countrySearch,
          onChange: setCountrySearch,
          options: searchedCountryOptions,
          placeholder: 'Search by name or code',
          'aria-label': 'Search countries',
        }}
        valueRenderer={() => (
          <Group variant="horizontal" spacing="xs" alignItems="center">
            <FlagIcon countryCode={selectedCountry.code as ComponentProps<typeof FlagIcon>['countryCode']} size="small" />
            <Icon type={isCountryMenuOpen ? 'chevron-up' : 'chevron-down'} size="small" color="inherit" />
            <Text textStyle="body2">+{selectedCountry.callingCode}</Text>
          </Group>
        )}
      />
    </Box>
  );

  return (
    <FormField
      {...fieldProps}
      labelProps={{ label }}
      isRequired
      error={isInvalid ? {} : undefined}
      size="large"
      data-testid={testId}
      render={({ ref: fieldRef, ...fieldProps }) => (
        <Group ref={fieldGroupRef} variant="horizontal" spacing="none" alignItems="center" width="full">
          {countryTrigger}
          <Box flex={1} position="relative" _focusWithin={{ zIndex: 1 }}>
            <TextField
              {...fieldProps}
              ref={mergeRefs(numberInputRef, fieldRef)}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={current.nationalNumber}
              onChange={handleNumberChange}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              placeholder=""
              data-testid={`${testId}-number`}
              data-private
              // No border, minimal padding - reads as one continuous number, not two adjacent boxes.
              style={{
                width: '100%',
                borderLeft: 'none',
                paddingLeft: '4px',
                borderRadius: `0 ${FIELD_BORDER_RADIUS} ${FIELD_BORDER_RADIUS} 0`,
              }}
            />
          </Box>
        </Group>
      )}
    />
  );
};
